import { Dispatch } from "redux";
import Config from "config";
import axios from "axios";

// Types
export interface SystemAdmin {
    id: number;
    name: string;
    email: string;
}

export interface ProjectAdmin {
    id: number;
    name: string;
    email: string;
}

export interface ApprovalStage {
    status_code: string;
    approver_user_id: any;
    approval_stage_id: number | any;
    request_id: number;
    workflow_stage_id: number;
    stage_order: number;
    approver_role: string;
    isFinalStage: boolean;
    status: string;
    approved_by: number | null;
    approved_at: string | null;
    comments: string | null;
}

export interface AuditLog {
    actor_user_id: number;
    actorName: string;
    action: string;
    CreatedAt: string;
    approverRole:string;
    name: string;
    nextApproverAssigned:any;
    chainUsers: any;
    chainName: any;
}

export interface CheckOnboardUserResult {
    totalCount: number;
    availableCount: number;
    notAvailableCount: number;
    availableUsers: { id: number; email: string; first_name: string; last_name: string }[];
    notAvailableEmails: string[];
}
export interface AuditLogValues {
  chainName: JSX.Element;
  chainUsers: any;
  action: string;
  CreatedAt: string;
  actorName: string;
  nextApproverAssigned: string;
  name: string;
}

export interface RequestStatus {
    value: string;
    label: string;
}

export interface RequestType {
    value: string;
    label: string;
}

export interface RequestDependenciesState {
    systemAdmins: SystemAdmin[];
    projectAdmins: ProjectAdmin[];
    approvalStages: any;
    auditLogs: AuditLog[];
    onboardUserCheck: CheckOnboardUserResult | null;
    requestStatuses: RequestStatus[];
    requestTypes: RequestType[];
    loading: boolean;
    error: string | null;
    activeRequestCount: number;
}

// Action Types
const FETCH_REQUEST_DEPENDENCIES = "FETCH_REQUEST_DEPENDENCIES";
const FETCH_REQUEST_DEPENDENCIES_SUCCESS = "FETCH_REQUEST_DEPENDENCIES_SUCCESS";
const FETCH_REQUEST_DEPENDENCIES_FAILURE = "FETCH_REQUEST_DEPENDENCIES_FAILURE";

const FETCH_PROJECT_ADMINS = "FETCH_PROJECT_ADMINS";
const FETCH_PROJECT_ADMINS_SUCCESS = "FETCH_PROJECT_ADMINS_SUCCESS";
const FETCH_PROJECT_ADMINS_FAILURE = "FETCH_PROJECT_ADMINS_FAILURE";

const FETCH_APPROVAL_STAGES = "FETCH_APPROVAL_STAGES";
const FETCH_APPROVAL_STAGES_SUCCESS = "FETCH_APPROVAL_STAGES_SUCCESS";
const FETCH_APPROVAL_STAGES_FAILURE = "FETCH_APPROVAL_STAGES_FAILURE";

const UPDATE_APPROVAL_STATUS = "UPDATE_APPROVAL_STATUS";
const UPDATE_APPROVAL_STATUS_SUCCESS = "UPDATE_APPROVAL_STATUS_SUCCESS";
const UPDATE_APPROVAL_STATUS_FAILURE = "UPDATE_APPROVAL_STATUS_FAILURE";

const UPDATE_REQUEST_APPROVER = "UPDATE_REQUEST_APPROVER";
const UPDATE_REQUEST_APPROVER_SUCCESS = "UPDATE_REQUEST_APPROVER_SUCCESS";
const UPDATE_REQUEST_APPROVER_FAILURE = "UPDATE_REQUEST_APPROVER_FAILURE";

const FETCH_AUDIT_REQUEST_APPROVAL_LOG = "FETCH_AUDIT_REQUEST_APPROVAL_LOG";
const FETCH_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS = "FETCH_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS";
const FETCH_AUDIT_REQUEST_APPROVAL_LOG_FAILURE = "FETCH_AUDIT_REQUEST_APPROVAL_LOG_FAILURE";

const CREATE_AUDIT_REQUEST_APPROVAL_LOG = "CREATE_AUDIT_REQUEST_APPROVAL_LOG";
const CREATE_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS = "CREATE_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS";
const CREATE_AUDIT_REQUEST_APPROVAL_LOG_FAILURE = "CREATE_AUDIT_REQUEST_APPROVAL_LOG_FAILURE";

const CHECK_ONBOARD_USER = "CHECK_ONBOARD_USER";
const CHECK_ONBOARD_USER_SUCCESS = "CHECK_ONBOARD_USER_SUCCESS";
const CHECK_ONBOARD_USER_FAILURE = "CHECK_ONBOARD_USER_FAILURE";

const FETCH_REQUEST_STATUSES = "FETCH_REQUEST_STATUSES";
const FETCH_REQUEST_STATUSES_SUCCESS = "FETCH_REQUEST_STATUSES_SUCCESS";
const FETCH_REQUEST_STATUSES_FAILURE = "FETCH_REQUEST_STATUSES_FAILURE";

const FETCH_REQUEST_TYPES = "FETCH_REQUEST_TYPES";
const FETCH_REQUEST_TYPES_SUCCESS = "FETCH_REQUEST_TYPES_SUCCESS";
const FETCH_REQUEST_TYPES_FAILURE = "FETCH_REQUEST_TYPES_FAILURE";

const FETCH_USER_ACTIVE_REQUESTS = "FETCH_USER_ACTIVE_REQUESTS";
const FETCH_USER_ACTIVE_REQUESTS_SUCCESS = "FETCH_USER_ACTIVE_REQUESTS_SUCCESS";
const FETCH_USER_ACTIVE_REQUESTS_FAILURE = "FETCH_USER_ACTIVE_REQUESTS_FAILURE";

// Action Creators
export const fetchSystemAdmins = () => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: FETCH_REQUEST_DEPENDENCIES });
        try {
            const [systemAdminsResponse] = await Promise.all([
                axios.get(`${Config.API_SERVICE_URL}/api/request/requestDetails/systemAdmins`)
            ]);

            dispatch({
                type: FETCH_REQUEST_DEPENDENCIES_SUCCESS,
                payload: {
                    systemAdmins: systemAdminsResponse.data || []
                }
            });
        } catch (error: any) {
            console.error("Error fetching request dependencies:", error);
            dispatch({
                type: FETCH_REQUEST_DEPENDENCIES_FAILURE,
                payload: error.message || "Failed to fetch request dependencies"
            });
        }
    };
};

export const getExternalProjectAdminByChain = (chainId: number | string) => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: FETCH_PROJECT_ADMINS });
        try {
            const response = await axios.get(
                `${Config.API_SERVICE_URL}/api/request/requestDetails/projectAdmins/${chainId}`
            );

            dispatch({
                type: FETCH_PROJECT_ADMINS_SUCCESS,
                payload: {
                    projectAdmins: response.data || []
                }
            });
        } catch (error: any) {
            console.error("Error fetching project admins:", error);
            dispatch({
                type: FETCH_PROJECT_ADMINS_FAILURE,
                payload: error.message || "Failed to fetch project admins"
            });
        }
    };
};

export const getRequestApprovalStages = (requestId: number | string) => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: FETCH_APPROVAL_STAGES });
        try {
            const response = await axios.get(
                `${Config.API_SERVICE_URL}/api/request/requestDetails/requestApprovalStages/${requestId}`
            );

            dispatch({
                type: FETCH_APPROVAL_STAGES_SUCCESS,
                payload: {
                    approvalStages: response.data || []
                }
            });
        } catch (error: any) {
            console.error("Error fetching approval stages:", error);
            dispatch({
                type: FETCH_APPROVAL_STAGES_FAILURE,
                payload: error.message || "Failed to fetch approval stages"
            });
        }
    };
};

export const updateRequestApprovalStatus = (
    requestApprovalStageId: number | string,
    status: string,
    reason: string,
    assignedTo : number | string,
) => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: UPDATE_APPROVAL_STATUS });
        try {
            const response = await axios.patch(
                `${Config.API_SERVICE_URL}/api/request/updateRequestStatus/${requestApprovalStageId}`,
                { status, requestApprovalStageId, reason, assignedTo }
            );

            dispatch({
                type: UPDATE_APPROVAL_STATUS_SUCCESS,
                payload: response.data
            });

            return response.data;
        } catch (error: any) {
            console.error("Error updating approval status:", error);
            dispatch({
                type: UPDATE_APPROVAL_STATUS_FAILURE,
                payload: error.response?.data?.errors ||
                    [error.message || "Failed to update approval status"]
            });
            throw error;
        }
    };
};

export const updateRequestApprover = (
    requestApprovalStageId: number | string,
    approverUser: number | string,
    approverPayload: any,
    requestType : string,
    assignedTo:number | string,
) => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: UPDATE_REQUEST_APPROVER });
        try {
            const response = await axios.patch(
                `${Config.API_SERVICE_URL}/api/request/updateRequestApprover/${requestApprovalStageId}`,
                { approverUser, approverPayload, requestType, assignedTo }
            );

            dispatch({
                type: UPDATE_REQUEST_APPROVER_SUCCESS,
                payload: response.data
            });

            return response.data;
        } catch (error: any) {
            console.error("Error updating request approver:", error);
            dispatch({
                type: UPDATE_REQUEST_APPROVER_FAILURE,
                payload: error.response?.data?.errors ||
                    [error.message || "Failed to update request approver"]
            });
            throw error;
        }
    };
};

export const fetchAuditRequestApprovalLog = (requestId: number | string) => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: FETCH_AUDIT_REQUEST_APPROVAL_LOG });
        try {
            const response = await axios.get(
                `${Config.API_SERVICE_URL}/api/request/auditRequestApprovalLog/${requestId}`
            );

            dispatch({
                type: FETCH_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS,
                payload: {
                    auditLogs: response.data || []
                }
            });
        } catch (error: any) {
            console.error("Error fetching audit request approval log:", error);
            dispatch({
                type: FETCH_AUDIT_REQUEST_APPROVAL_LOG_FAILURE,
                payload: error.message || "Failed to fetch audit request approval log"
            });
        }
    };
};

export const createRequestAuditLog = (
    requestId: number,
    requestForJson: object,
    action: string,
    approvalStageId : number
) => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: CREATE_AUDIT_REQUEST_APPROVAL_LOG });
        try {
            const response = await axios.post(
                `${Config.API_SERVICE_URL}/api/request/requestAuditLog`,
                {
                    requestId,
                    requestForJson,
                    action,
                    approvalStageId
                }
            );

            dispatch({
                type: CREATE_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS,
                payload: response.data
            });

            return response.data;
        } catch (error: any) {
            console.error("Error creating audit request approval log:", error);
            dispatch({
                type: CREATE_AUDIT_REQUEST_APPROVAL_LOG_FAILURE,
                payload: error.response?.data?.errors ||
                    [error.message || "Failed to create audit request approval log"]
            });
            throw error;
        }
    };
};

export const checkOnboardUser = (emails: string | string[]) => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: CHECK_ONBOARD_USER });
        try {
            const response = await axios.post(
                `${Config.API_SERVICE_URL}/api/request/checkOnboardUser`,
                { emails }
            );

            dispatch({
                type: CHECK_ONBOARD_USER_SUCCESS,
                payload: response.data
            });

            return response.data;
        } catch (error: any) {
            console.error("Error checking onboard users:", error);
            dispatch({
                type: CHECK_ONBOARD_USER_FAILURE,
                payload: error.response?.data?.errors ||
                    [error.message || "Failed to check onboard users"]
            });
            throw error;
        }
    };
};

export const fetchRequestStatuses = () => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: FETCH_REQUEST_STATUSES });
        try {
            const response = await axios.get(
                `${Config.API_SERVICE_URL}/api/request/requestStatus/getAllRequestStatus`
            );

            dispatch({
                type: FETCH_REQUEST_STATUSES_SUCCESS,
                payload: {
                    requestStatuses: response.data || []
                }
            });
        } catch (error: any) {
            console.error("Error fetching request statuses:", error);
            dispatch({
                type: FETCH_REQUEST_STATUSES_FAILURE,
                payload: error.message || "Failed to fetch request statuses"
            });
        }
    };
};

export const fetchRequestTypes = () => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: FETCH_REQUEST_TYPES });
        try {
            const response = await axios.get(
                `${Config.API_SERVICE_URL}/api/request/requestTypes/getAllRequestTypes`
            );
            dispatch({
                type: FETCH_REQUEST_TYPES_SUCCESS,
                payload: { requestTypes: response.data || [] }
            });
        } catch (error: any) {
            dispatch({
                type: FETCH_REQUEST_TYPES_FAILURE,
                payload: error.message || "Failed to fetch request types"
            });
        }
    };
};

export const fetchUserActiveRequests = () => {
    return async (dispatch: Dispatch) => {
        dispatch({ type: FETCH_USER_ACTIVE_REQUESTS });
        try {
            const response = await axios.get(
                `${Config.API_SERVICE_URL}/api/request/userRequest/getActiveRequestCount`
            );
            dispatch({
                type: FETCH_USER_ACTIVE_REQUESTS_SUCCESS,
                payload: { activeRequestCount: response.data.requestCount }
            });
        } catch (error: any) {
            dispatch({
                type: FETCH_USER_ACTIVE_REQUESTS_FAILURE,
                payload: error.message || "Failed to fetch user active requests"
            });
        }
    };
};

// Initial State
const initialState: RequestDependenciesState = {
    systemAdmins: [],
    projectAdmins: [],
    approvalStages: [],
    auditLogs: [],
    onboardUserCheck: null,
    requestStatuses: [],
    requestTypes: [],
    loading: false,
    error: null,
    activeRequestCount: 0,
};

// Reducer
export const requestDependenciesReducer = (
    state = initialState,
    action: any
): RequestDependenciesState => {
    switch (action.type) {
        case FETCH_REQUEST_DEPENDENCIES:
        case FETCH_PROJECT_ADMINS:
        case FETCH_APPROVAL_STAGES:
        case UPDATE_APPROVAL_STATUS:
        case UPDATE_REQUEST_APPROVER:
        case FETCH_AUDIT_REQUEST_APPROVAL_LOG:
        case CREATE_AUDIT_REQUEST_APPROVAL_LOG:
        case CHECK_ONBOARD_USER:
            return {
                ...state,
                loading: true,
                error: null
            };

        case FETCH_REQUEST_DEPENDENCIES_SUCCESS:
            return {
                ...state,
                loading: false,
                systemAdmins: action.payload.systemAdmins,
                error: null
            };

        case FETCH_PROJECT_ADMINS_SUCCESS:
            return {
                ...state,
                loading: false,
                projectAdmins: action.payload.projectAdmins,
                error: null
            };

        case FETCH_APPROVAL_STAGES_SUCCESS:
            return {
                ...state,
                loading: false,
                approvalStages: action.payload.approvalStages,
                error: null
            };

        case FETCH_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS:
            return {
                ...state,
                loading: false,
                auditLogs: action.payload.auditLogs,
                error: null
            };

        case UPDATE_APPROVAL_STATUS_SUCCESS:
        case UPDATE_REQUEST_APPROVER_SUCCESS:
        case CREATE_AUDIT_REQUEST_APPROVAL_LOG_SUCCESS:
            return {
                ...state,
                loading: false,
                error: null
            };

        case CHECK_ONBOARD_USER_SUCCESS:
            return {
                ...state,
                loading: false,
                onboardUserCheck: action.payload,
                error: null
            };

        case FETCH_REQUEST_DEPENDENCIES_FAILURE:
        case FETCH_PROJECT_ADMINS_FAILURE:
        case FETCH_APPROVAL_STAGES_FAILURE:
        case UPDATE_APPROVAL_STATUS_FAILURE:
        case UPDATE_REQUEST_APPROVER_FAILURE:
        case FETCH_AUDIT_REQUEST_APPROVAL_LOG_FAILURE:
        case CREATE_AUDIT_REQUEST_APPROVAL_LOG_FAILURE:
        case CHECK_ONBOARD_USER_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload
            };
            case FETCH_REQUEST_STATUSES: 
            return {
                ...state,
                loading: true,
                error: null
            };
            case FETCH_REQUEST_STATUSES_SUCCESS:
                return {
                    ...state,
                    loading: false,
                    requestStatuses: action.payload.requestStatuses,
                    error: null
                };

            case FETCH_REQUEST_STATUSES_FAILURE:
                return { ...state, loading: false, error: action.payload };

            case FETCH_REQUEST_TYPES:
                return { ...state, loading: true, error: null };

            case FETCH_REQUEST_TYPES_SUCCESS:
                return { ...state, loading: false, requestTypes: action.payload.requestTypes, error: null };

            case FETCH_REQUEST_TYPES_FAILURE:
                return { ...state, loading: false, error: action.payload };

            case FETCH_USER_ACTIVE_REQUESTS:
                return { ...state, loading: true, error: null };

            case FETCH_USER_ACTIVE_REQUESTS_SUCCESS:
                return { ...state, loading: false, activeRequestCount: action.payload.activeRequestCount, error: null };

            case FETCH_USER_ACTIVE_REQUESTS_FAILURE:
                return { ...state, loading: false, error: action.payload };

        default:
            return state;
    }
};

export default requestDependenciesReducer;